import { Download, HelpCircle, Settings, User as UserIcon, Users, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { db } from '../services/db';
import { captureSilentError } from '../services/posthog';
import { useUIStore } from '../stores/useUIStore';
import { FriendRequest, User } from '../types';
import { FriendRequests } from './profile/FriendRequests';
import { FriendsList } from './profile/FriendsList';
import { ProfileTab } from './profile/ProfileTab';
import { SettingsTab } from './profile/SettingsTab';
import { UserSearch } from './profile/UserSearch';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  shoutCount: number;
  onInstallApp: () => void;
  onOpenSupport: () => void;
  onOpenAdmin: () => void;
  onFriendClick?: (friendId: string) => void;
  notificationPermission: NotificationPermission;
  onRequestNotificationPermission: () => Promise<void>;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({ 
    isOpen, 
    onClose, 
    shoutCount,
    onInstallApp,
    onOpenSupport,
    onOpenAdmin,
    onFriendClick,
    notificationPermission,
    onRequestNotificationPermission
}) => {
  const { currentUser } = useAuth();

  const [activeTab, setActiveTab] = useState<'profile' | 'settings' | 'friends'>('profile');
  
  const [incomingRequests, setIncomingRequests] = useState<{request: FriendRequest, fromUser: User}[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<{request: FriendRequest, toUser: User}[]>([]);
  const [friends, setFriends] = useState<User[]>([]);
  const [isLoadingSocial, setIsLoadingSocial] = useState(false);
  const [socialError, setSocialError] = useState<string | null>(null);

  const { isInstalled, deferredPrompt, promptInstall, isMobile } = usePWAInstall();

  // -- LOAD DATA --
  const loadSocialData = async () => {
    if (!currentUser) return;
    setIsLoadingSocial(true);
    setSocialError(null);
    try {
        // Fetch requests
        const incoming = await db.getIncomingRequests(currentUser.uid);
        setIncomingRequests(incoming);

        const outgoing = await db.getOutgoingRequests(currentUser.uid);
        setOutgoingRequests(outgoing);

        // Fetch friends
        if (currentUser.friends.length > 0) {
             const friendsData = await db.getUsersByIds(currentUser.friends);
             setFriends(friendsData);
        } else {
             setFriends([]);
        }
    } catch (e) {
        captureSilentError("ProfileModal.loadSocialData", e);
        setSocialError("Failed to load friends. Please try again.");
    } finally {
        setIsLoadingSocial(false);
    }
  };

  // Init on Open
  useEffect(() => {
    if (isOpen && currentUser) {
      loadSocialData();
    }
  }, [isOpen, currentUser?.friends]);


  if (!isOpen || !currentUser) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-dark/60 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div 
        className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-brand-cream-dark flex justify-between items-center bg-white">
           <h3 className="font-display font-extrabold text-xl text-brand-dark">My Profile</h3>
           <button onClick={onClose} className="p-2 hover:bg-brand-cream-dark rounded-full" aria-label="Close"><X size={20}/></button>
        </div>

        {/* Tabs */}
        <div className="flex p-2 gap-2 bg-brand-cream-dark border-b border-brand-cream-dark">
            <button 
                onClick={() => setActiveTab('profile')}
                className={`flex-1 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${activeTab === 'profile' ? 'bg-white shadow text-brand-dark' : 'text-brand-dark-light hover:text-brand-dark'}`}
            >
                <UserIcon size={16} />
                Profile
            </button>
            <button 
                onClick={() => setActiveTab('settings')}
                className={`flex-1 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${activeTab === 'settings' ? 'bg-white shadow text-brand-dark' : 'text-brand-dark-light hover:text-brand-dark'}`}
            >
                <Settings size={16} />
                Settings
            </button>
            <button 
                onClick={() => setActiveTab('friends')}
                className={`flex-1 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${activeTab === 'friends' ? 'bg-white shadow text-brand-dark' : 'text-brand-dark-light hover:text-brand-dark'}`}
            >
                <Users size={16} />
                Friends
                {incomingRequests.length > 0 && <span className="w-4 h-4 bg-brand-red text-white text-[10px] flex items-center justify-center rounded-full">{incomingRequests.length}</span>}
            </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 bg-brand-cream">
          
          {activeTab === 'profile' && (
            <ProfileTab 
                shoutCount={shoutCount}
                onFriendsClick={() => setActiveTab('friends')}
                onShoutsClick={() => {
                    useUIStore.getState().setFilters({
                        categories: [],
                        statuses: [],
                        userIds: [currentUser.uid]
                    });
                    useUIStore.getState().setViewMode('list');
                    onClose();
                }}
            />
          )}

          {activeTab === 'settings' && (
             <SettingsTab 
                onOpenAdmin={onOpenAdmin}
                notificationPermission={notificationPermission}
                onRequestNotificationPermission={onRequestNotificationPermission}
                onClose={onClose}
             />
          )}

          {/* --- TAB: FRIENDS --- */}
          {activeTab === 'friends' && (
             <div className="p-6 space-y-8">
                {isLoadingSocial ? (
                    <div className="space-y-6 animate-pulse">
                        {/* Search Shimmer */}
                        <div className="space-y-3">
                            <div className="h-4 w-24 bg-brand-cream-dark rounded" />
                            <div className="h-12 w-full bg-brand-cream-dark rounded-xl" />
                        </div>
                        
                        {/* Friends List Shimmer */}
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <div className="h-4 w-20 bg-brand-cream-dark rounded" />
                                <div className="h-4 w-8 bg-brand-cream-dark rounded-full" />
                            </div>
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="flex items-center justify-between p-3 bg-white border border-brand-cream-dark rounded-2xl">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-brand-cream-dark" />
                                        <div className="space-y-2">
                                            <div className="h-4 w-32 bg-brand-cream-dark rounded" />
                                            <div className="h-3 w-20 bg-brand-cream-dark rounded" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : socialError ? (
                    <div className="flex flex-col items-center justify-center p-8 bg-white border border-brand-red/20 rounded-2xl text-center space-y-4">
                        <p className="text-brand-red font-bold">{socialError}</p>
                        <button 
                            onClick={loadSocialData}
                            className="px-6 py-2 bg-brand-red text-white font-bold rounded-lg shadow hover:bg-brand-red-hover transition active:scale-95"
                        >
                            Retry
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Search */}
                        <UserSearch 
                            friends={friends}
                            outgoingRequests={outgoingRequests}
                            incomingRequests={incomingRequests}
                            onRefresh={loadSocialData}
                        />

                        {/* Friends List */}
                        <FriendsList friends={friends} onFriendClick={onFriendClick} />

                        {/* Pending Requests Sent */}
                        <FriendRequests 
                            outgoingRequests={outgoingRequests}
                            incomingRequests={incomingRequests}
                            onRefresh={loadSocialData}
                        />
                    </>
                )}
             </div>
          )}
        </div>

        {/* Footer (Persistent) */}
        <div className="p-4 border-t border-brand-cream-dark bg-white space-y-3">
             <button
                onClick={onOpenSupport}
                className="w-full py-3 bg-white border-2 border-brand-cream-dark text-brand-dark font-bold rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-transform hover:bg-brand-cream"
            >
                <HelpCircle size={20} />
                Support & Feedback
            </button>

            {!isInstalled && isMobile && (
                <button
                    onClick={() => {
                        if (deferredPrompt) {
                            promptInstall();
                        } else {
                            onInstallApp();
                        }
                    }}
                    className="w-full py-3 bg-brand-cream text-brand-dark font-bold rounded-xl border-2 border-brand-dark/10 flex items-center justify-center gap-2 active:scale-95 transition-transform"
                >
                    <Download size={20} />
                    Install App
                </button>
            )}


        </div>
      </div>
    </div>
  );
};

